import { useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

const CHECK_UPDATE_MS = 60 * 60 * 1000; // 1h : recherche d'une nouvelle version
const RETRY_RELOAD_MS = 30 * 1000; // 30s : réessai de rechargement quand une MàJ est en attente

const FIELD_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'];

/**
 * Enregistre le service worker et applique les mises à jour automatiquement,
 * SANS interrompre une saisie en cours.
 *
 * Fonctionnement :
 *  - On recherche une nouvelle version au chargement, toutes les heures, et au
 *    retour sur l'app (changement d'onglet / retour de veille).
 *  - Quand une nouvelle version est prête (onNeedRefresh), on ne recharge pas
 *    tout de suite : on attend qu'aucun <form> ne soit en cours de saisie.
 *  - Le rechargement (skipWaiting + reload) n'a lieu que lorsque c'est sûr.
 *
 * Garde-fou : un formulaire est considéré « en cours » dès qu'un de ses champs
 * a été modifié par l'utilisateur, et jusqu'à sa soumission (submit) ou son reset.
 * Si l'utilisateur ne soumet jamais, la mise à jour s'appliquera à la prochaine
 * ouverture de l'app — aucune donnée saisie n'est perdue.
 */
const PWAUpdatePrompt = () => {
  // true dès qu'une nouvelle version du SW est prête à être activée
  const needRefreshRef = useRef(false);
  // true si l'utilisateur a modifié un champ d'un <form> sans l'avoir soumis
  const formDirtyRef = useRef(false);

  const { updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      // registration.update() est asynchrone et peut être rejeté avec une
      // InvalidStateError quand le SW est dans un état invalide (installation
      // en cours, registration devenue « redundant »…). On ignore ce rejet
      // pour ne pas générer d'erreur non gérée : la prochaine tentative
      // (intervalle ou retour sur l'app) se fera dans un état valide.
      const safeUpdate = () => {
        try {
          registration.update()?.catch(() => {});
        } catch {
          /* SW dans un état invalide : on réessaiera plus tard */
        }
      };

      setInterval(safeUpdate, CHECK_UPDATE_MS);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          safeUpdate();
        }
      });
    },
    onNeedRefresh() {
      needRefreshRef.current = true;
    },
  });

  useEffect(() => {
    const isInForm = (el: EventTarget | null) =>
      el instanceof Element && FIELD_TAGS.includes(el.tagName) && el.closest('form');

    // Un form devient « sale » dès la première saisie utilisateur…
    const markDirty = (e: Event) => {
      if (isInForm(e.target)) formDirtyRef.current = true;
    };
    // …et « propre » à la soumission ou au reset.
    const markClean = () => {
      formDirtyRef.current = false;
    };

    document.addEventListener('input', markDirty, true);
    document.addEventListener('submit', markClean, true);
    document.addEventListener('reset', markClean, true);

    // Recharge uniquement si une MàJ est prête ET qu'aucun form n'est en cours de saisie.
    const timer = setInterval(() => {
      if (needRefreshRef.current && !formDirtyRef.current) {
        updateServiceWorker(true); // skipWaiting + reload piloté
      }
    }, RETRY_RELOAD_MS);

    return () => {
      document.removeEventListener('input', markDirty, true);
      document.removeEventListener('submit', markClean, true);
      document.removeEventListener('reset', markClean, true);
      clearInterval(timer);
    };
  }, [updateServiceWorker]);

  return null;
};

export default PWAUpdatePrompt;
